/**
 *
 * @module customerServiceClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/customer-service-code.md
 *
 */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import HttpClient from './../platforms/httpClient';
import Token from './../token/entry';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import CustomerServiceClientConfiguration from './customerServiceClientConfiguration';
import CustomerServiceClientEndpoint from './customerServiceClientEndpoint';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const CustomerServiceClientDustUrnReference = DustUrnReference.services.customerService.customerServiceClient;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class CustomerServiceClient
 * @desc Provides a data client that can be used to access customer services.
 *
 */
export default class CustomerServiceClient {

    /**
     *
     * @access private
     * @param {CustomerServiceClientConfiguration} customerServiceClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(customerServiceClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'CustomerServiceClient(customerServiceClientConfiguration, logger, httpClient)',
            get customerServiceClientConfiguration() {
                return `${this.ctor} needs CustomerServiceClientConfiguration instance`;
            }
        };

        instanceStrict(
            customerServiceClientConfiguration,
            CustomerServiceClientConfiguration,
            errorMessage.customerServiceClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {CustomerServiceClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = customerServiceClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(CustomerServiceClient.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken - The current access token.
     * @desc Creates a customer service code for this user for use in support.
     * @returns {Promise<String>}
     *
     */
    createSupportCode(accessToken) {

        const errorMessage = {
            method: 'customerServiceClient.createSupportCode(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            CustomerServiceClientEndpoint.createSupportCode
        );

        logger.info(CustomerServiceClient.name, 'Requesting customer service support code.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: CustomerServiceClient.name,
            urn: CustomerServiceClientDustUrnReference.createSupportCode,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {CustomerServiceClientEndpoint} rel
     * @param {Object} body (optional)
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, body) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestBody = body ? JSON.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi,
            accessToken.token);

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/customerService/customerServiceClient.js